import React, { useState } from 'react'
import './EmotionTracker.css'

interface EmotionRecord {
  id: number
  date: string
  emotion: string
  intensity: number
  description: string
  tags: string[]
  weather: string
  activities: string[]
  partner: string
}

interface EmotionStat {
  emotion: string
  count: number
  percentage: number
  color: string
}

const EmotionTracker: React.FC = () => {
  const [records, setRecords] = useState<EmotionRecord[]>([
    {
      id: 1,
      date: '2024-01-28',
      emotion: '开心',
      intensity: 9,
      description: '今天和你一起做饭，虽然厨房有点乱，但是很开心。你做的菜特别好吃！',
      tags: ['做饭', '在家', '温馨'],
      weather: '晴天',
      activities: ['做饭', '聊天', '看电影'],
      partner: '小红'
    },
    {
      id: 2,
      date: '2024-01-27',
      emotion: '浪漫',
      intensity: 8,
      description: '晚上一起看星星，你指着北斗七星给我讲故事。那一刻觉得时间都停止了。',
      tags: ['看星星', '户外', '夜晚'],
      weather: '晴天',
      activities: ['散步', '看星星', '聊天'],
      partner: '小明'
    },
    {
      id: 3,
      date: '2024-01-26',
      emotion: '感动',
      intensity: 10,
      description: '生病的时候你一直照顾我，给我买药，做粥。真的很感动，有你真好。',
      tags: ['生病', '照顾', '关爱'],
      weather: '阴天',
      activities: ['休息', '吃药', '聊天'],
      partner: '小红'
    },
    {
      id: 4,
      date: '2024-01-25',
      emotion: '兴奋',
      intensity: 9,
      description: '收到了你准备的惊喜礼物！没想到你还记得我随口说过想要的那本书。',
      tags: ['惊喜', '礼物', '书'],
      weather: '晴天',
      activities: ['收礼物', '阅读', '拥抱'],
      partner: '小明'
    },
    {
      id: 5,
      date: '2024-01-24',
      emotion: '温馨',
      intensity: 8,
      description: '雨天在家里一起听音乐，你靠在我肩膀上。这种安静的陪伴让人很安心。',
      tags: ['下雨', '音乐', '陪伴'],
      weather: '雨天',
      activities: ['听音乐', '聊天', '拥抱'],
      partner: '小红'
    }
  ])

  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingRecord, setEditingRecord] = useState<EmotionRecord | null>(null)
  const [selectedEmotion, setSelectedEmotion] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [selectedIntensity, setSelectedIntensity] = useState(5)

  const handleAddEmotion = (formData: FormData) => {
    const newRecord: EmotionRecord = {
      id: Date.now(),
      date: formData.get('date') as string,
      emotion: formData.get('emotion') as string,
      intensity: selectedIntensity,
      description: formData.get('description') as string,
      tags: (formData.get('tags') as string).split(',').map(tag => tag.trim()).filter(tag => tag),
      weather: formData.get('weather') as string,
      activities: Array.from(formData.getAll('activities')) as string[],
      partner: formData.get('partner') as string
    }
    setRecords([newRecord, ...records])
    setShowAddForm(false)
    setSelectedIntensity(5)
  }

  const handleEditRecord = (record: EmotionRecord) => {
    setEditingRecord(record)
    setSelectedIntensity(record.intensity)
    setShowEditForm(true)
  }

  const handleUpdateRecord = (formData: FormData) => {
    if (!editingRecord) return
    
    const updatedRecord: EmotionRecord = {
      ...editingRecord,
      date: formData.get('date') as string,
      emotion: formData.get('emotion') as string,
      intensity: selectedIntensity,
      description: formData.get('description') as string,
      tags: (formData.get('tags') as string).split(',').map(tag => tag.trim()).filter(tag => tag),
      weather: formData.get('weather') as string,
      activities: Array.from(formData.getAll('activities')) as string[],
      partner: formData.get('partner') as string
    }
    
    setRecords(records.map(record => 
      record.id === editingRecord.id ? updatedRecord : record
    ))
    setShowEditForm(false)
    setEditingRecord(null)
    setSelectedIntensity(5)
  }

  const handleDeleteRecord = (recordId: number) => {
    if (window.confirm('确定要删除这条情感记录吗？')) {
      setRecords(records.filter(record => record.id !== recordId))
    }
  }

  const emotions = [
    { name: '开心', color: '#FFD700', icon: '😊' },
    { name: '浪漫', color: '#FF69B4', icon: '💕' },
    { name: '感动', color: '#87CEEB', icon: '🥺' },
    { name: '兴奋', color: '#FF6347', icon: '🤩' },
    { name: '温馨', color: '#98FB98', icon: '🥰' },
    { name: '幸福', color: '#DDA0DD', icon: '😍' },
    { name: '平静', color: '#B0E0E6', icon: '😌' },
    { name: '思念', color: '#F0E68C', icon: '🥲' }
  ]

  const activities = ['做饭', '看电影', '散步', '旅行', '运动', '学习', '购物', '聊天', '游戏', '阅读']
  const weathers = ['晴天', '阴天', '雨天', '雪天', '多云']

  // 计算情感统计
  const emotionStats: EmotionStat[] = emotions.map(emotion => {
    const count = records.filter(record => record.emotion === emotion.name).length
    const percentage = records.length > 0 ? Math.round((count / records.length) * 100) : 0
    return {
      emotion: emotion.name,
      count,
      percentage,
      color: emotion.color
    }
  }).filter(stat => stat.count > 0).sort((a, b) => b.count - a.count)

  // 过滤记录
  const filteredRecords = records.filter(record => {
    const matchesEmotion = !selectedEmotion || record.emotion === selectedEmotion
    const matchesDate = !dateFilter || record.date.includes(dateFilter)
    return matchesEmotion && matchesDate
  })

  // 获取情感颜色
  const getEmotionColor = (emotionName: string) => {
    return emotions.find(e => e.name === emotionName)?.color || '#666'
  }

  // 获取情感图标
  const getEmotionIcon = (emotionName: string) => {
    return emotions.find(e => e.name === emotionName)?.icon || '😊'
  }

  // 计算平均强度
  const averageIntensity = records.length > 0 
    ? Math.round(records.reduce((sum, record) => sum + record.intensity, 0) / records.length)
    : 0

  // 获取最近7天的情感趋势
  const getRecentTrend = () => {
    const last7Days = records
      .filter(record => {
        const recordDate = new Date(record.date)
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        return recordDate >= weekAgo
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    
    return last7Days
  }

  const recentTrend = getRecentTrend()

  return (
    <div className="emotion-tracker">
      <div className="tracker-header">
        <h2 className="tracker-title">情感记录 💕</h2>
        <button 
          className="btn btn-primary add-emotion-btn"
          onClick={() => setShowAddForm(true)}
        >
          ➕ 记录心情
        </button>
      </div>

      {/* 情感统计概览 */}
      <div className="emotion-overview">
        <div className="overview-stats">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-number">{records.length}</div>
              <div className="stat-label">总记录数</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <div className="stat-number">{averageIntensity}/10</div>
              <div className="stat-label">平均强度</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-content">
              <div className="stat-number">{emotionStats[0]?.emotion || '暂无'}</div>
              <div className="stat-label">最常情感</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-number">{recentTrend.length}</div>
              <div className="stat-label">本周记录</div>
            </div>
          </div>
        </div>
      </div>

      {/* 情感分布图表 */}
      <div className="emotion-chart">
        <h3 className="chart-title">情感分布</h3>
        <div className="chart-container">
          <div className="pie-chart">
            {emotionStats.map((stat, index) => {
              const angle = (stat.percentage / 100) * 360
              const rotation = emotionStats.slice(0, index).reduce((sum, s) => sum + (s.percentage / 100) * 360, 0)
              
              return (
                <div
                  key={stat.emotion}
                  className="pie-slice"
                  style={{
                    background: `conic-gradient(${stat.color} ${angle}deg, transparent ${angle}deg)`,
                    transform: `rotate(${rotation}deg)`
                  }}
                ></div>
              )
            })}
          </div>
          <div className="chart-legend">
            {emotionStats.map(stat => (
              <div key={stat.emotion} className="legend-item">
                <div 
                  className="legend-color"
                  style={{ backgroundColor: stat.color }}
                ></div>
                <span className="legend-text">
                  {getEmotionIcon(stat.emotion)} {stat.emotion} ({stat.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 最近趋势 */}
      <div className="recent-trend">
        <h3 className="trend-title">最近7天情感趋势</h3>
        <div className="trend-chart">
          {recentTrend.map(record => (
            <div key={record.id} className="trend-item">
              <div className="trend-date">{record.date.split('-').slice(1).join('/')}</div>
              <div 
                className="trend-emotion"
                style={{ backgroundColor: getEmotionColor(record.emotion) }}
              >
                {getEmotionIcon(record.emotion)}
              </div>
              <div className="trend-intensity">
                <div className="intensity-bar">
                  <div 
                    className="intensity-fill"
                    style={{ 
                      width: `${record.intensity * 10}%`,
                      backgroundColor: getEmotionColor(record.emotion)
                    }}
                  ></div>
                </div>
                <span className="intensity-value">{record.intensity}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 筛选器 */}
      <div className="emotion-filters">
        <div className="filter-group">
          <label>情感筛选：</label>
          <select 
            className="filter-select"
            value={selectedEmotion}
            onChange={(e) => setSelectedEmotion(e.target.value)}
          >
            <option value="">全部情感</option>
            {emotions.map(emotion => (
              <option key={emotion.name} value={emotion.name}>
                {emotion.icon} {emotion.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>日期筛选：</label>
          <input
            type="month"
            className="filter-input"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>
      </div>

      {/* 情感记录列表 */}
      <div className="emotion-records">
        <h3 className="records-title">情感记录</h3>
        <div className="records-list">
          {filteredRecords.map(record => (
            <div key={record.id} className="emotion-record">
              <div className="record-header">
                <div className="record-emotion">
                  <span 
                    className="emotion-icon"
                    style={{ backgroundColor: getEmotionColor(record.emotion) }}
                  >
                    {getEmotionIcon(record.emotion)}
                  </span>
                  <span className="emotion-name">{record.emotion}</span>
                </div>
                <div className="record-date">{record.date}</div>
              </div>
              
              <div className="record-intensity">
                <span className="intensity-label">强度：</span>
                <div className="intensity-stars">
                  {[...Array(10)].map((_, i) => (
                    <span 
                      key={i}
                      className={`star ${i < record.intensity ? 'filled' : ''}`}
                      style={{ color: i < record.intensity ? getEmotionColor(record.emotion) : '#ddd' }}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
                <span className="intensity-number">{record.intensity}/10</span>
              </div>
              
              <p className="record-description">{record.description}</p>
              
              <div className="record-details">
                <div className="detail-item">
                  <span className="detail-label">天气：</span>
                  <span className="detail-value">{record.weather}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">伙伴：</span>
                  <span className="detail-value">{record.partner}</span>
                </div>
              </div>
              
              <div className="record-tags">
                {record.tags.map(tag => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
              
              <div className="record-activities">
                <span className="activities-label">活动：</span>
                {record.activities.map(activity => (
                  <span key={activity} className="activity-tag">{activity}</span>
                ))}
              </div>
              
              <div className="record-actions">
                <button 
                  className="action-btn edit-btn"
                  onClick={() => handleEditRecord(record)}
                >
                  ✏️ 编辑
                </button>
                <button 
                  className="action-btn delete-btn"
                  onClick={() => handleDeleteRecord(record.id)}
                >
                  🗑️ 删除
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 添加情感记录模态框 */}
      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="add-emotion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>记录心情</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <form className="add-emotion-form" onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                handleAddEmotion(formData)
              }}>
                <div className="form-row">
                  <div className="form-group">
                  <label>日期</label>
                  <input type="date" name="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} />
                </div>
                <div className="form-group">
                  <label>情感</label>
                  <select name="emotion" className="input">
                    <option value="">选择情感</option>
                    {emotions.map(emotion => (
                      <option key={emotion.name} value={emotion.name}>
                        {emotion.icon} {emotion.name}
                      </option>
                    ))}
                  </select>
                </div>
                </div>
                
                <div className="form-group">
                  <label>强度 (1-10)</label>
                  <div className="intensity-selector">
                    {[...Array(10)].map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`intensity-btn ${selectedIntensity === i + 1 ? 'active' : ''}`}
                        onClick={() => setSelectedIntensity(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>描述</label>
                  <textarea name="description" className="textarea" placeholder="描述这个时刻的感受..."></textarea>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>天气</label>
                    <select name="weather" className="input">
                      <option value="">选择天气</option>
                      {weathers.map(weather => (
                        <option key={weather} value={weather}>{weather}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>伙伴</label>
                    <input type="text" name="partner" className="input" placeholder="和谁在一起" />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>活动</label>
                  <div className="activities-selector">
                    {activities.map(activity => (
                      <label key={activity} className="activity-checkbox">
                        <input type="checkbox" name="activities" value={activity} />
                        <span className="checkmark"></span>
                        <span>{activity}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>标签</label>
                  <input type="text" name="tags" className="input" placeholder="用逗号分隔多个标签" />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                    取消
                  </button>
                  <button type="submit" className="btn btn-primary">
                    保存记录
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 编辑情感记录模态框 */}
      {showEditForm && editingRecord && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="add-emotion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>编辑心情记录</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <form className="add-emotion-form" onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                handleUpdateRecord(formData)
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>日期</label>
                    <input type="date" name="date" className="input" defaultValue={editingRecord.date} />
                  </div>
                  <div className="form-group">
                    <label>情感</label>
                    <select name="emotion" className="input" defaultValue={editingRecord.emotion}>
                      <option value="">选择情感</option>
                      {emotions.map(emotion => (
                        <option key={emotion.name} value={emotion.name}>
                          {emotion.icon} {emotion.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>强度 (1-10)</label>
                  <div className="intensity-selector">
                    {[...Array(10)].map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`intensity-btn ${selectedIntensity === i + 1 ? 'active' : ''}`}
                        onClick={() => setSelectedIntensity(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>描述</label>
                  <textarea name="description" className="textarea" defaultValue={editingRecord.description}></textarea>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>天气</label>
                    <select name="weather" className="input" defaultValue={editingRecord.weather}>
                      <option value="">选择天气</option>
                      {weathers.map(weather => (
                        <option key={weather} value={weather}>{weather}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>伙伴</label>
                    <input type="text" name="partner" className="input" defaultValue={editingRecord.partner} />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>活动</label>
                  <div className="activities-selector">
                    {activities.map(activity => (
                      <label key={activity} className="activity-checkbox">
                        <input 
                          type="checkbox" 
                          name="activities" 
                          value={activity}
                          defaultChecked={editingRecord.activities.includes(activity)}
                        />
                        <span className="checkmark"></span>
                        <span>{activity}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>标签</label>
                  <input type="text" name="tags" className="input" defaultValue={editingRecord.tags.join(', ')} />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowEditForm(false)
                    setEditingRecord(null)
                    setSelectedIntensity(5)
                  }}>
                    取消
                  </button>
                  <button type="submit" className="btn btn-primary">
                    更新记录
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmotionTracker